import React, { useMemo, useState } from "react";
import { CpButton, CpCard } from "@components";
import { useForm } from "react-hook-form";
import { SortController } from "../common/sort/sort-controller.component";
import { InputConditionController } from "./input-condition-controller.component";
import { conditionOptsByDataType } from "./input-filter.helper";
import { FilterFooter } from "../common/filter-footer.component";
import { isEmpty } from "lodash";

const maxConditions = 2;
export function InputFilter({
  columnSchema,
  onClose,
  filterControl,
  hideConditionOptions,
}) {
  const filterData = filterControl.filters[columnSchema.id]?.data;
  const [extraConditions, setExtraConditions] = useState(
    () => filterData?.slice(1).map((data, i) => `conditions.${i + 1}`) || [],
  );
  const conditionOptions = useMemo(() => {
    const opts =
      conditionOptsByDataType[columnSchema.dataType] ||
      conditionOptsByDataType.string;
    if (isEmpty(hideConditionOptions)) {
      return opts;
    } else {
      return opts.filter((o) => !hideConditionOptions.includes(o.id));
    }
  }, [hideConditionOptions, columnSchema]);

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      sort: filterControl.sortData[columnSchema.id]?.direction,
      conditions: {
        // We only support 2 conditions atm, otherwise we would iterate over filterData
        0: {
          condition:
            getConditionValueById(filterData?.[0]?.condition) ||
            conditionOptions[0],
          filterValue: filterData?.[0]?.filterValue,
        },
        1: {
          condition: getConditionValueById(filterData?.[1]?.condition),
          filterValue: filterData?.[1]?.filterValue,
          operator: filterData?.[1]?.operator,
        },
      },
    },
  });

  function getConditionValueById(conditionId) {
    if (!conditionId) return null;
    return (
      conditionOptions.find((c) => c.id === conditionId) || {
        id: conditionId,
        name: conditionId,
      }
    );
  }

  function onSubmit(data) {
    const filterData = Object.keys(data.conditions)
      .map((i) => {
        const { operator, filterValue, condition } = data.conditions[i];
        const trimmedValue = condition?.disableInput ? "" : filterValue?.trim();
        if (!condition || (!trimmedValue && !condition?.disableInput))
          return null;
        return {
          filterValue: trimmedValue,
          condition: condition.id,
          ...(operator ? { operator } : {}),
        };
      })
      .filter((d) => !!d);
    filterControl.applyFilter(columnSchema.id, {
      filterData,
      sortDir: data.sort,
    });
    onClose();
  }

  function onClear() {
    reset({
      sort: null,
      conditions: {
        0: {
          condition: conditionOptions[0],
        },
      },
    });
    setExtraConditions([]);
  }

  return (
    <CpCard>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CpCard.Body>
          {columnSchema.sort !== false && (
            <>
              <SortController
                control={control}
                dataType={columnSchema.dataType}
                ascLabel={columnSchema.sort.ascLabel}
                descLabel={columnSchema.sort.descLabel}
              />
              <div className="cp-divider" />
            </>
          )}
          <InputConditionController
            conditionOptions={conditionOptions}
            control={control}
            dataType={columnSchema.dataType}
            name="conditions.0"
          />
          {extraConditions.length > 0 && (
            <div className="flex flex-col cp-mt-16 cp-gap-16">
              {extraConditions.map((name) => (
                <InputConditionController
                  conditionOptions={conditionOptions}
                  control={control}
                  dataType={columnSchema.dataType}
                  key={name}
                  name={name}
                  onRemove={() =>
                    setExtraConditions((val) => val.filter((n) => n !== name))
                  }
                  requireOperator
                />
              ))}
            </div>
          )}
          {extraConditions.length + 1 < maxConditions && (
            <CpButton
              aria-label="Add condition"
              btnType="tertiary"
              className="cp-mt-16"
              icon="add-small"
              onClick={() => {
                if (extraConditions.length + 1 < maxConditions) {
                  setExtraConditions((val) => [
                    ...val,
                    "conditions." + (val.length + 1),
                  ]);
                }
              }}
              type="button"
            >
              Add condition
            </CpButton>
          )}
        </CpCard.Body>
        <FilterFooter onCancel={onClose} onClear={onClear} />
      </form>
    </CpCard>
  );
}
